import { Injectable } from '@angular/core';
import { MountingMaterialServiceDsService } from '@app/core/data-services/mounting-material-ds/mounting-material-service-ds.service';
import { ActiveWorkCenterService } from '@app/core/workcenter';
import { MaterialTableTreeNode } from '@app/modules/mounting/models/material-table.model';
import {
  MaterialAvailableAtLocation,
  MaterialService,
  MaterialType,
  MountContainerResponse,
  MountingKpiValue,
  Quantity,
  SecondaryMaterial,
  SecondaryMaterialMounted,
  SecondaryMaterialService
} from 'chronos-core-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SecondaryMaterialDsService {
  constructor(
    private secondaryMaterialService: SecondaryMaterialService,
    private mountingMaterialServiceDsService: MountingMaterialServiceDsService,
    private activeWorkCenterService: ActiveWorkCenterService,
    private materialService: MaterialService
  ) {}

  public getSecondaryMaterialOverviewData(): Observable<MaterialTableTreeNode[]> {
    return this.getSecondaryMaterialBlocks().pipe(
      map((secondaryMaterial) => this.mountingMaterialServiceDsService.mapSecondaryMaterial(secondaryMaterial.materialBlocks))
    );
  }

  public getSecondaryMountedMaterialData(): Observable<MaterialTableTreeNode[]> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();

    return this.secondaryMaterialService
      .getMountedMaterials2({ workCenterId })
      .pipe(map((mountedMaterial) => this.mountingMaterialServiceDsService.mapSecondaryMaterial(mountedMaterial)));
  }

  public getSecondaryMaterialBlocks(): Observable<SecondaryMaterial> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.secondaryMaterialService.getSecondaryMaterial({ workCenterId });
  }

  public dismountContainer(
    containerId: number,
    mountedMaterialId: number,
    newContainerBomQuantity: Quantity,
    originalConsumedBomQuantity: Quantity,
    originalMountedBomQuantity: Quantity,
    runId: number = null,
    shouldPrintLabel: boolean,
    workCenterId: number = null
  ): Observable<void> {
    workCenterId = workCenterId ?? this.activeWorkCenterService.getWorkCenterId();
    return this.materialService.dismountContainer({
      workCenterId,
      body: {
        containerId,
        mountedMaterialId,
        newContainerBomQuantity,
        originalConsumedBomQuantity,
        originalMountedBomQuantity,
        runId,
        shouldPrintLabel
      }
    });
  }

  public mountSecondary(
    identificationCode: string,
    containerId: number,
    productionOrderId: number,
    whiteWasteQuantity?: Quantity
  ): Observable<MountContainerResponse> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.materialService.mountContainer({
      workCenterId,
      body: {
        containerId,
        identificationCode,
        materialType: MaterialType.Secondary,
        productionOrderId,
        whiteWasteQuantity
      }
    });
  }

  public getNextContainerForDismount(): Observable<SecondaryMaterialMounted> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.secondaryMaterialService.getNextSecondaryContainerForDismount({ workCenterId });
  }

  public consumeMaterial(
    containerId: number,
    quantity: number,
    selectedWorkCenterId?: number,
    runId?: number,
    billOfMaterialId?: number
  ): Observable<void> {
    const workCenterId = selectedWorkCenterId || this.activeWorkCenterService.getWorkCenterId();
    return this.secondaryMaterialService.consume({
      workCenterId,
      body: {
        containerId,
        quantity,
        runId,
        billOfMaterialId
      }
    });
  }

  public getMaterialList(): Observable<MaterialAvailableAtLocation> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.secondaryMaterialService.getMaterialAvailable({ workCenterId });
  }

  public getMaterialListForProductionOrder(workCenterId: number, productionOrderId: number): Observable<MaterialAvailableAtLocation> {
    return this.secondaryMaterialService.getMaterialAvailableForProductionOrder2({
      workCenterId,
      productionOrderId
    });
  }

  public getMaterialListForProductionOrderAndArticle(
    workCenterId: number,
    productionOrderId: number,
    articleId: number
  ): Observable<MaterialAvailableAtLocation> {
    return this.secondaryMaterialService.getMaterialAvailableForProductionOrderAndArticle({
      workCenterId,
      productionOrderId,
      articleId
    });
  }

  public getSecondaryToDismount(): Observable<MountingKpiValue> {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    return this.secondaryMaterialService.toBeDismountedKpi({ workCenterId });
  }
}
